import useTranslation from 'next-translate/useTranslation'
import { useRouter } from 'next/router'

import { getBrandName, getProductBaseSKU, getSimplifiedProductName } from '../../../lib/text'
import { Product, Supplier } from '../../../utils/types/Product'
import useProductSchemaProps from '../../hooks/useProductSchemaProps'
import useBreadcrumbSchemaProps from '../../hooks/useBreadcrumbSchemaProps'
import { getSupplierName } from '../../../lib/supplier'
import { CountryAndLocale, LocaleHreflangMap } from '../../../external'
import { getSEOUrls } from '../../../lib/urls'
import { getOrderedProductCategories } from '../../../lib/product'
import { AggregateRatingProductSchemaProps, ReviewMinimized } from '../../../utils/types/Review'
import { TOTAL_SATISFIED_CUSTOMERS } from '../../../utils/constants'

interface UseProductSeoDataParams {
  product: Product
  reviews?: ReviewMinimized[]
  aggregateRating?: AggregateRatingProductSchemaProps
  supplier?: Supplier
  localizedUrls?: LocaleHreflangMap
}

const useProductSeoData = (params: UseProductSeoDataParams) => {
  const { product, reviews, aggregateRating, supplier, localizedUrls } = params

  const { locale: countryAndLocaleString } = useRouter()
  const { t } = useTranslation('products')

  const productBaseSKU = getProductBaseSKU(product)
  const brandName = product ? getBrandName(product) : ''
  const supplierName = getSupplierName(supplier)
  const productName = getSimplifiedProductName({ product, supplier })

  const breadcrumbCategories = product
    ? getOrderedProductCategories(product).slice(0, 3)
    : []

  const seoTitle = t('meta:productTitle', { supplierName: brandName || supplierName, productName, productBaseSKU })
  const seoDescription = t('meta:productDescription', {
    brand: brandName || supplierName,
    baseSKU: productBaseSKU,
    productName,
    customerCount: TOTAL_SATISFIED_CUSTOMERS,
  })

  // Escape double quotes for schema description
  const schemaDescription = seoDescription.replace(/"/g, '\\"')

  const productSchemaProps = useProductSchemaProps({
    product,
    supplier,
    reviews,
    aggregateRating,
    description: schemaDescription,
  })

  const breadcrumbSchemaProps = useBreadcrumbSchemaProps({
    product,
    categories: breadcrumbCategories,
    productName: seoTitle,
  })

  const { canonicalUrl, hreflangUrls } = getSEOUrls({
    localizedUrls,
    countryAndLocaleString: countryAndLocaleString as CountryAndLocale,
    supplier,
  })

  return {
    seoTitle,
    seoDescription,
    productSchemaProps,
    breadcrumbSchemaProps,
    canonicalUrl,
    hreflangUrls,
  }
}

export default useProductSeoData
